import { Form, Formik, FormikConfig } from 'formik';

import { Button } from 'components/atoms';
import {
  DescriptiveInputWrapper,
  FormInput,
} from 'components/molecules';
import 'components/atoms/inputs/image/ImageInput.scss';
import 'components/molecules/info-box/modules/image/InfoBoxImage.scss';
import cs from 'classnames';import i18next from 'i18next';
import _ from 'lodash';
import { useStoreState } from 'providers/StoreStateContext';
import { ChangeEvent, useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  PiFloppyDisk,
  PiImage,
  PiImageSquareLight,
  PiPencil,
  PiPlus,
  PiProhibit,
} from 'react-icons/pi';
import { IClientModuleConfigurationForm } from 'services/settings/client-module/types';
import { useGetContractTemplateLightList } from 'services/templates/contract-template';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { II18nextPrefix } from 'types/components/components.interface';
import {
  isImageFile,
  isSquareImageFile,
} from 'utils/components/inputs/image';
import { booleanSelectOptions } from 'utils/components/inputs/select';
import { boolean, object, string } from 'yup';
import { IFormProps } from '../types';

const CLIENT_IMAGE_ACCEPT =
  'image/jpeg,image/jpg,image/png,image/svg+xml,image/webp,image/gif,.svg';

export const clientModuleConfigurationInitialValues: IClientModuleConfigurationForm =
  {
    contractTemplateId: '',
    clientName: '',
    useMagellupLogo: true,
    autoCreateAccount: false,
  };

const clientModuleConfigurationValidator = object().shape({
  contractTemplateId: string().required(i18next.t('formik-error.required')),
  clientName: string().required(i18next.t('formik-error.required')),
  autoCreateAccount: boolean().required(i18next.t('formik-error.required')),
});

export interface ISettingsFormProps
  extends FormikConfig<IClientModuleConfigurationForm>,
    IFormProps,
    II18nextPrefix {}

interface ISimpleImageUploadProps {
  previewUrl?: string;
  file?: File;
  disabled?: boolean;
  onFileChange: (file: File | undefined) => void;
  requireSquare?: boolean;
  /** Free layout keeps natural aspect ratio (main logo). Square uses ImageInput trigger styles (favicon). */
  layout?: 'free' | 'square';
}

const SimpleImageUpload = ({
  previewUrl,
  file,
  disabled,
  onFileChange,
  requireSquare = false,
  layout = 'square',
}: ISimpleImageUploadProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const localPreviewUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : undefined),
    [file]
  );

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  const displayUrl = localPreviewUrl ?? previewUrl;

  const openFilePicker = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.currentTarget.files?.[0];
    event.currentTarget.value = '';

    if (!selectedFile) {
      return;
    }

    if (!isImageFile(selectedFile)) {
      toast.error(t('toast-message.unsupported-image-format'));
      return;
    }

    if (requireSquare) {
      try {
        const isSquare = await isSquareImageFile(selectedFile);
        if (!isSquare) {
          toast.error(t('error-code.IMAGE_MUST_BE_SQUARE'));
          return;
        }
      } catch {
        toast.error(t('toast-message.unsupported-image-format'));
        return;
      }
    }

    onFileChange(selectedFile);
  };

  const fileInput = (
    <input
      ref={inputRef}
      className='hidden'
      type='file'
      accept={CLIENT_IMAGE_ACCEPT}
      disabled={disabled}
      onChange={event => {
        void handleChange(event);
      }}
    />
  );

  if (layout === 'free') {
    return (
      <div className='flex w-full flex-col items-center gap-3 py-4'>
        <div
          className={cs('flex items-center justify-center w-full', {
            'opacity-60': disabled,
          })}>
          {displayUrl ? (
            <img
              src={displayUrl}
              alt=''
              loading='lazy'
              style={{
                maxWidth: 280,
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          ) : (
            <PiImageSquareLight className='text-4xl opacity-40' />
          )}
        </div>
        {!disabled && (
          <Button type='button' isClear onClick={openFilePicker}>
            <Button.Text text={t('button.change-image')} />
            <Button.Icon icon={<PiImage />} />
          </Button>
        )}
        {fileInput}
      </div>
    );
  }

  const triggerClassNames = cs(['image-input-trigger'], {
    editable: !disabled,
    disable: disabled,
  });

  return (
    <div className='flex w-full justify-center p-30'>
      <div className='info-box-image'>
        <div
          className={triggerClassNames}
          onClick={openFilePicker}
          role={disabled ? undefined : 'button'}
          tabIndex={disabled ? undefined : 0}
          onKeyDown={event => {
            if (disabled) {
              return;
            }

            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openFilePicker();
            }
          }}>
          {displayUrl ? (
            <img src={displayUrl} alt='' loading='lazy' />
          ) : (
            <PiImageSquareLight className='placeholder' />
          )}
          {!disabled && (
            <div className='hover-overlay'>
              {displayUrl ? <PiPencil /> : <PiPlus className='new' />}
            </div>
          )}
          {fileInput}
        </div>
      </div>
    </div>
  );
};

const ApplicationSettingsForm = ({
  i18nextPrefix,
  topbarContent,
  tittle,
  status,
  initialValues = clientModuleConfigurationInitialValues,
  disableTabs,
  backPath,
  customBackItem,
  ...props
}: ISettingsFormProps) => {
  const { t } = useTranslation();

  const contractTemplates = useGetContractTemplateLightList();

  const { ownerLogo } = useStoreState();

  return (
    <Formik
      {...props}
      validateOnChange
      validateOnBlur
      validateOnMount
      validationSchema={clientModuleConfigurationValidator}
      initialStatus={status?.displayMode}
      enableReinitialize
      initialValues={initialValues}>
      {({ setValues, values, setFieldValue }) => (
        <>
          <Form className='flex w-full h-full gap-[30px] flex-wrap'>
            <div className='flex flex-col gap-[30px] w-[370px]'>
              <DescriptiveInputWrapper
                header={t('setting.client-module.brand')}
                description={t('setting.client-module.brandDescription')}>
                <FormInput.Text
                  name='clientName'
                  label={t('input.label.name')}
                />
                <FormInput.Select
                  name='useMagellupLogo'
                  label={t('input.label.useMagellupLogo')}
                  options={booleanSelectOptions}
                  isClearable={false}
                />
                <FormInput.Select
                  name='autoCreateAccount'
                  label={t('input.label.autoCreateAccount')}
                  options={booleanSelectOptions}
                  isClearable={false}
                />
                <SimpleImageUpload
                  previewUrl={
                    values.useMagellupLogo
                      ? ownerLogo?.pictureLink ?? values.magelupLogoPath
                      : values.clientLogoPath ?? values.currentLogoPath
                  }
                  file={values.file}
                  disabled={values.useMagellupLogo}
                  layout='free'
                  onFileChange={file => setFieldValue('file', file)}
                />
              </DescriptiveInputWrapper>
              <DescriptiveInputWrapper
                header={t('setting.client-module.favicon')}
                description={t('setting.client-module.faviconDescription')}>
                <SimpleImageUpload
                  previewUrl={values.faviconPath}
                  file={values.faviconFile}
                  requireSquare
                  layout='square'
                  onFileChange={file => setFieldValue('faviconFile', file)}
                />
              </DescriptiveInputWrapper>
              <DescriptiveInputWrapper
                header={t('setting.client-module.contractTemplate')}
                description={t(
                  'setting.client-module.contractTemplateDescription'
                )}>
                <FormInput.Select
                  name='contractTemplateId'
                  label={t('input.label.contract-template')}
                  options={contractTemplates.data?.map(c => ({
                    value: c.id,
                    label: c.title,
                  }))}
                  isClearable={false}
                />
              </DescriptiveInputWrapper>
            </div>

            {!_.isEqual(
              {
                ...initialValues,
                file: undefined,
                faviconFile: undefined,
              },
              {
                ...values,
                file: undefined,
                faviconFile: undefined,
              }
            ) ||
            values.file !== undefined ||
            values.faviconFile !== undefined ? (
              <div className='buttons'>
                <Button
                  variant={BUTTON_VARIANT.warning}
                  onClick={() => setValues(initialValues)}>
                  <Button.Icon icon={<PiProhibit transform='scale(-1,1)' />} />
                  <Button.Text text={t(`enum.TEMPLATE_TYPE_SETTINGS.cancle`)} />
                </Button>
                <Button type='submit' variant={BUTTON_VARIANT.submit}>
                  <Button.Icon
                    icon={<PiFloppyDisk transform='scale(-1,1)' />}
                  />
                  <Button.Text text={t(`enum.TEMPLATE_TYPE_SETTINGS.submit`)} />
                </Button>
              </div>
            ) : null}
          </Form>
        </>
      )}
    </Formik>
  );
};

export default ApplicationSettingsForm;
